/**
 * International geo redirect pop-up for the US storefront (kindscience.com).
 *
 * Catches organic/direct international visitors who land on the US store.
 * Uses Shopify IP geolocation — no third-party geo API.
 *
 * Flow:
 *   1. Skip bots, theme editor, global subdomain, saved cookie
 *   2. utm_source on kindscience.com → stay on US store, no pop-up (no redirect)
 *   3. Detect country via browsing_context_suggestions.json
 *   4. US / Canada → do nothing
 *   5. Supported country (50-country launch list) → supported pop-up → global
 *   6. Unsupported country → Coming soon pop-up (Instagram + stay on US)
 *   7. Save choice in ks_region_preference cookie (30 days)
 *
 * Console: verbose logs only when config.debug is true (myshopify.com staging).
 * Production logs errors only.
 *
 * Staging QA (myshopify.com):
 *   ?ks_geo_test=GB&ks_geo_test_name=United%20Kingdom   → supported pop-up
 *   ?ks_geo_test=IN&ks_geo_test_name=India                 → Coming soon pop-up
 *   ?ks_geo_clear=1
 */
(function () {
  'use strict';

  var LOG = '[KS Geo]';
  var config = window.KS_GEO_CONFIG;
  var popupIsVisible = false;
  var shouldSuppressKlaviyo = false;
  var detectedCountry = null;
  var activeVariant = null;
  var popupTimer = null;
  var UTM_SESSION_KEY = 'ks_geo_utm_session';

  if (!config || !config.enabled) return;

  // Register before async geo fetch — Klaviyo can fire early.
  installKlaviyoSuppression();

  whenDomReady(function () {
    if (!bindPopupEvents()) return;
    runGeoFlow();
  });

  // -------------------------------------------------------------------------
  // Main flow
  // -------------------------------------------------------------------------

  function runGeoFlow() {
    if (clearQaCookieIfRequested()) return;

    if (config.isDesignMode) return;

    if (window.location.hostname.indexOf('global.') === 0) return;

    // SEO: never show pop-up or redirect crawlers.
    if (isBot()) return;

    // Return visitors: honour 30-day preference cookie.
    var preference = getCookie(config.cookieName);
    if (preference === 'international') {
      redirectToGlobal('cookie');
      return;
    }
    if (preference === 'us') return;

    // Paid media on US store: utm_source → stay on domestic, suppress pop-up only.
    if (hasUtmSource()) {
      debugLog('utm_source detected. Staying on US site, no pop-up.');
      return;
    }

    // Staging only: ?ks_geo_test=GB skips IP lookup.
    var qaCountry = getQaOverrideCountry();
    if (qaCountry) {
      debugWarn('QA override active:', qaCountry);
      handleDetectedCountry(qaCountry);
      return;
    }

    detectCountry()
      .then(function (country) {
        // Fail closed: no country from Shopify → no pop-up.
        if (!country || !country.isoCode) return;
        handleDetectedCountry(country);
      })
      .catch(function (err) {
        console.error(LOG, 'Country detection failed.', err);
      });
  }

  /**
   * Catch-all routing:
   *   US / Canada                 → no pop-up
   *   Supported launch markets    → supported pop-up (Yes → global)
   *   Everyone else               → Coming soon pop-up (Instagram + stay on US)
   */
  function handleDetectedCountry(country) {
    detectedCountry = country;
    debugLog('Country:', country.isoCode, country.name);

    if (isDomestic(country.isoCode)) {
      debugLog('Domestic visitor. No pop-up.');
      return;
    }

    var variant = resolvePopupVariant(country.isoCode);
    if (!variant) {
      debugLog('Unsupported country and Coming soon pop-up disabled. No pop-up.');
      return;
    }

    shouldSuppressKlaviyo = true;
    activeVariant = variant;

    trackEvent('ks_geo_popup_scheduled', {
      country_code: country.isoCode,
      country_name: country.name,
      variant: variant
    });

    popupTimer = setTimeout(function () {
      showPopup(country, variant);
    }, config.displayDelayMs);
  }

  function resolvePopupVariant(isoCode) {
    if (isSupportedCountry(isoCode)) return 'supported';
    if (config.enableUnsupportedPopup) return 'unsupported';
    return null;
  }

  function isSupportedCountry(isoCode) {
    var list = config.supportedCountries || [];
    return list.indexOf(isoCode) !== -1;
  }

  // -------------------------------------------------------------------------
  // IP detection (Shopify native)
  // -------------------------------------------------------------------------

  /**
   * Shopify reads visitor IP server-side and returns country in
   * browsing_context_suggestions.json. We do not fall back to Liquid
   * localization.country — on a US-only store that is usually "US" for everyone.
   */
  function detectCountry() {
    var url = countrySuggestionsUrl();

    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('browsing_context_suggestions HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        debugLog('Geo response:', data);

        var detected = data && data.detected_values && data.detected_values.country;
        if (detected && detected.handle) {
          return normalizeCountry(detected.handle, detected.name);
        }

        // Alternate response shape from Shopify.
        var suggestions = data && data.suggestions;
        if (suggestions && suggestions.length) {
          for (var i = 0; i < suggestions.length; i++) {
            var part = suggestions[i].parts && suggestions[i].parts.country;
            if (part && part.handle) {
              return normalizeCountry(part.handle, part.name);
            }
          }
        }

        debugWarn('No country in Shopify geo response. Failing closed.');
        return null;
      });
  }

  function countrySuggestionsUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'browsing_context_suggestions.json';
  }

  function normalizeCountry(isoCode, name) {
    return {
      isoCode: String(isoCode).toUpperCase(),
      name: name || isoCode
    };
  }

  function isDomestic(isoCode) {
    return config.domesticCountries.indexOf(isoCode) !== -1;
  }

  // -------------------------------------------------------------------------
  // Pop-up UI
  // -------------------------------------------------------------------------

  function bindPopupEvents() {
    var overlay = document.getElementById('ks-geo-popup-overlay');
    if (!overlay) {
      console.error(LOG, 'Pop-up markup not found.');
      return false;
    }

    overlay.addEventListener('click', function (e) {
      // Backdrop click does not dismiss — user must choose explicitly.
      if (e.target === overlay) return;

      var action = e.target.closest('[data-ks-geo-action]');
      if (!action) return;

      if (action.dataset.ksGeoAction === 'accept') handleAccept();
      if (action.dataset.ksGeoAction === 'instagram') handleInstagram(e);
      if (action.dataset.ksGeoAction === 'dismiss') {
        handleDismiss(action.classList.contains('ks-geo-popup__close') ? 'close' : 'link');
      }
    });

    // Escape = same as "stay on US site" (sets cookie).
    document.addEventListener('keydown', function (e) {
      if (!popupIsVisible || e.key !== 'Escape') return;
      handleDismiss('escape');
    });

    return true;
  }

  function showPopup(country, variant) {
    if (popupIsVisible) return;

    var overlay = document.getElementById('ks-geo-popup-overlay');
    if (!overlay) return;

    var supportedPanel = overlay.querySelector('[data-ks-geo-variant="supported"]');
    var unsupportedPanel = overlay.querySelector('[data-ks-geo-variant="unsupported"]');

    if (supportedPanel) supportedPanel.hidden = variant !== 'supported';
    if (unsupportedPanel) unsupportedPanel.hidden = variant !== 'unsupported';

    // Point aria-labelledby at the visible headline.
    overlay.setAttribute(
      'aria-labelledby',
      variant === 'unsupported' ? 'ks-geo-popup-headline-unsupported' : 'ks-geo-popup-headline'
    );

    // Country name only applies to the supported body copy.
    if (variant === 'supported') {
      var nameEls = overlay.querySelectorAll('[data-ks-geo-country-name]');
      for (var i = 0; i < nameEls.length; i++) {
        nameEls[i].textContent = country.name;
      }
    }

    overlay.removeAttribute('hidden');
    overlay.classList.add('is-visible');
    document.body.classList.add('ks-geo-popup-open');
    popupIsVisible = true;

    trackEvent('ks_geo_popup_shown', {
      country_code: country.isoCode,
      country_name: country.name,
      variant: variant
    });
  }

  function hidePopup() {
    var overlay = document.getElementById('ks-geo-popup-overlay');
    if (!overlay) return;

    if (popupTimer) {
      clearTimeout(popupTimer);
      popupTimer = null;
    }

    overlay.classList.remove('is-visible');
    overlay.setAttribute('hidden', '');
    document.body.classList.remove('ks-geo-popup-open');
    popupIsVisible = false;
    activeVariant = null;
  }

  function handleAccept() {
    // Only the supported pop-up offers Yes → global.
    if (activeVariant !== 'supported') return;

    setPreferenceCookie('international');
    hidePopup();

    trackEvent('ks_geo_popup_accept', {
      country_code: detectedCountry && detectedCountry.isoCode,
      country_name: detectedCountry && detectedCountry.name,
      variant: 'supported'
    });

    redirectToGlobal('popup_accept');
  }

  /**
   * Unsupported pop-up primary CTA: open Instagram in a new tab,
   * remember "stay on US" so the pop-up does not return for 30 days.
   */
  function handleInstagram(e) {
    setPreferenceCookie('us');

    trackEvent('ks_geo_popup_instagram', {
      country_code: detectedCountry && detectedCountry.isoCode,
      country_name: detectedCountry && detectedCountry.name,
      variant: 'unsupported'
    });

    hidePopup();
    // Let the default <a target="_blank"> navigation proceed.
  }

  function handleDismiss(source) {
    setPreferenceCookie('us');
    hidePopup();

    trackEvent('ks_geo_popup_dismiss', {
      source: source,
      country_code: detectedCountry && detectedCountry.isoCode,
      country_name: detectedCountry && detectedCountry.name,
      variant: activeVariant
    });
  }

  // -------------------------------------------------------------------------
  // Klaviyo suppression
  // -------------------------------------------------------------------------

  function installKlaviyoSuppression() {
    window.addEventListener('klaviyoForms', function (e) {
      if (!shouldSuppressKlaviyo || !e.detail || e.detail.type !== 'open') return;

      if (window.klaviyo && typeof window.klaviyo.closeForm === 'function') {
        window.klaviyo.closeForm(e.detail.formId);
      }
    });
  }

  // -------------------------------------------------------------------------
  // Redirect + cookies
  // -------------------------------------------------------------------------

  function redirectToGlobal(reason) {
    var url = (config.globalUrl || '').trim();

    if (!/^https?:\/\//i.test(url)) {
      console.error(LOG, 'Invalid globalUrl. Aborting redirect.');
      return;
    }

    trackEvent('ks_geo_redirect', { reason: reason, destination: url });
    window.location.assign(url);
  }

  /**
   * ks_region_preference values:
   *   "international" → auto-redirect to global on future visits
   *   "us"            → suppress pop-up for 30 days
   */
  function setPreferenceCookie(value) {
    if (value !== 'international' && value !== 'us') return; 
    setCookie(config.cookieName, value, config.cookieDays);
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    var parts = [
      name + '=' + encodeURIComponent(value),
      'expires=' + expires,
      'path=/',
      'SameSite=Lax'
    ];

    var domain = getCookieDomain();
    if (domain) parts.push('domain=' + domain);
    if (window.location.protocol === 'https:') parts.push('Secure');

    document.cookie = parts.join('; ');
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function getCookieDomain() {
    // Share preference across kindscience.com subdomains in production.
    if (window.location.hostname.endsWith('kindscience.com')) {
      return '.kindscience.com';
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Exclusions
  // -------------------------------------------------------------------------

  // function hasUtmSource() {
  //   try {
  //     return new URLSearchParams(window.location.search).has('utm_source');
  //   } catch (e) {
  //     return window.location.search.indexOf('utm_source') !== -1;
  //   }
  // }

  function hasUtmSource() {
    try {
      if (sessionStorage.getItem(UTM_SESSION_KEY) === '1') {
        return true;
      }

      var hasUtm = new URLSearchParams(window.location.search).has('utm_source');

      if (hasUtm) {
        sessionStorage.setItem(UTM_SESSION_KEY, '1');
        return true;
      }

      return false;
    } catch (e) {
      var hasUtm = window.location.search.indexOf('utm_source') !== -1;

      if (hasUtm) {
        try {
          sessionStorage.setItem(UTM_SESSION_KEY, '1');
        } catch (_) {}
      }

      return hasUtm;
    }
  }

  function isBot() {
    var ua = navigator.userAgent || '';
    return /googlebot|bingbot|duckduckbot|bot|crawler|spider|scraper|shopify/i.test(ua);
  }

  // -------------------------------------------------------------------------
  // Staging QA helpers (gated by config.debug)
  // -------------------------------------------------------------------------

  function getQaOverrideCountry() {
    if (!config.debug) return null;

    try {
      var params = new URLSearchParams(window.location.search);
      var code = params.get('ks_geo_test');
      if (!code) return null;
      return normalizeCountry(code, params.get('ks_geo_test_name'));
    } catch (e) {
      return null;
    }
  }

  function clearQaCookieIfRequested() {
    if (!config.debug) return false;

    try {
      var params = new URLSearchParams(window.location.search);
      if (!params.has('ks_geo_clear')) return false;

      setCookie(config.cookieName, '', -1);
      params.delete('ks_geo_clear');
      var next = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.location.replace(next);
      return true;
    } catch (e) {
      return false;
    }
  }

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------

  function whenDomReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function trackEvent(eventName, payload) {
    if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;
    window.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
  }

  // Staging-only console helpers — no output on kindscience.com production.
  function debugLog() {
    if (!config.debug) return;
    var args = [LOG].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function debugWarn() {
    if (!config.debug) return;
    var args = [LOG].concat(Array.prototype.slice.call(arguments));
    console.warn.apply(console, args);
  }
})();
